import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import { animate, motion, useInView, useReducedMotion } from 'motion/react';
import { formatMoney } from '@dezza/shared';
import { cn } from '../lib/cn';
import { easings } from '../lib/motion';
import { Delta, Skeleton } from './display';

type Tone = 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'accent' | 'neutral';

/** Cor de linha/traço por tom (todas >= 3:1 sobre a superfície nos dois temas). */
const TONE_STROKE: Record<Tone, string> = {
  primary: 'var(--color-chart-1)',
  success: 'var(--color-success)',
  warning: 'var(--color-warning)',
  danger: 'var(--color-danger)',
  info: 'var(--color-info)',
  accent: 'var(--color-accent)',
  neutral: 'var(--color-chart-6)',
};

// ---------------------------------------------------------------- Sparkline
/** Caminho suave sem "passar do ponto" (interpolação monotônica de Fritsch-Carlson). */
function monotonePath(pts: [number, number][]): string {
  const n = pts.length;
  if (n === 0) return '';
  if (n === 1) return `M${pts[0]![0]},${pts[0]![1]}`;
  const dx: number[] = [];
  const m: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    dx[i] = pts[i + 1]![0] - pts[i]![0];
    m[i] = dx[i] === 0 ? 0 : (pts[i + 1]![1] - pts[i]![1]) / dx[i]!;
  }
  const t: number[] = new Array(n).fill(0);
  t[0] = m[0]!;
  t[n - 1] = m[n - 2]!;
  for (let i = 1; i < n - 1; i++) t[i] = m[i - 1]! * m[i]! <= 0 ? 0 : (m[i - 1]! + m[i]!) / 2;
  for (let i = 0; i < n - 1; i++) {
    if (m[i] === 0) {
      t[i] = 0;
      t[i + 1] = 0;
      continue;
    }
    const a = t[i]! / m[i]!;
    const b = t[i + 1]! / m[i]!;
    const s = a * a + b * b;
    if (s > 9) {
      const tau = 3 / Math.sqrt(s);
      t[i] = tau * a * m[i]!;
      t[i + 1] = tau * b * m[i]!;
    }
  }
  let d = `M${pts[0]![0].toFixed(2)},${pts[0]![1].toFixed(2)}`;
  for (let i = 0; i < n - 1; i++) {
    const [x0, y0] = pts[i]!;
    const [x1, y1] = pts[i + 1]!;
    const h = dx[i]! / 3;
    d += `C${(x0 + h).toFixed(2)},${(y0 + t[i]! * h).toFixed(2)} ${(x1 - h).toFixed(2)},${(y1 - t[i + 1]! * h).toFixed(2)} ${x1.toFixed(2)},${y1.toFixed(2)}`;
  }
  return d;
}

function useWidth<T extends HTMLElement>(fixed?: number): [RefObject<T | null>, number] {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(fixed ?? 0);
  useLayoutEffect(() => {
    if (fixed !== undefined || !ref.current) return;
    const el = ref.current;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => setWidth(Math.round(entry!.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [fixed]);
  return [ref, fixed ?? width];
}

export interface SparklineProps {
  data: number[];
  /** Altura em px (padrão 36). */
  height?: number;
  /** Largura fixa em px. Sem ela, ocupa a largura do contêiner. */
  width?: number;
  tone?: Tone;
  /** Cor CSS que substitui o tom (ex.: 'var(--color-chart-3)'). */
  color?: string;
  /** Área com degradê abaixo da linha. */
  fill?: boolean;
  strokeWidth?: number;
  /** Ponto no último valor. */
  showDot?: boolean;
  /** Desenha a linha ao montar (desligado com reduzir movimento). */
  animate?: boolean;
  className?: string;
  /** Com rótulo vira imagem acessível; sem, é decorativo. */
  'aria-label'?: string;
}

/** Minigráfico de linha em SVG puro (tendência de um KPI). */
export function Sparkline({
  data,
  height = 36,
  width: fixedWidth,
  tone = 'primary',
  color,
  fill = true,
  strokeWidth = 2,
  showDot = true,
  animate: animated = true,
  className,
  'aria-label': ariaLabel,
}: SparklineProps) {
  const [ref, width] = useWidth<HTMLDivElement>(fixedWidth);
  const reduce = useReducedMotion();
  const gid = `spark${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const stroke = color ?? TONE_STROKE[tone];

  const { line, area, last } = useMemo(() => {
    const values = data.filter((v) => Number.isFinite(v));
    if (width <= 0 || values.length === 0) return { line: '', area: '', last: null as [number, number] | null };
    const pad = strokeWidth + (showDot ? 3 : 1);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    const innerW = Math.max(1, width - pad * 2);
    const innerH = Math.max(1, height - pad * 2);
    const step = values.length > 1 ? innerW / (values.length - 1) : 0;
    const pts = values.map<[number, number]>((v, i) => [pad + i * step, max === min ? height / 2 : pad + innerH - ((v - min) / span) * innerH]);
    const d = monotonePath(pts);
    const lastPt = pts[pts.length - 1]!;
    return { line: d, area: `${d}L${lastPt[0].toFixed(2)},${height}L${pts[0]![0].toFixed(2)},${height}Z`, last: lastPt };
  }, [data, width, height, strokeWidth, showDot]);

  const draw = animated && !reduce;
  return (
    <div ref={ref} className={cn('w-full', className)} style={{ height, width: fixedWidth }}>
      {line ? (
        <svg
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          role={ariaLabel ? 'img' : undefined}
          aria-label={ariaLabel}
          aria-hidden={ariaLabel ? undefined : true}
          className="block overflow-visible"
        >
          {fill ? (
            <>
              <defs>
                <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={stroke} stopOpacity={0.26} />
                  <stop offset="100%" stopColor={stroke} stopOpacity={0} />
                </linearGradient>
              </defs>
              <motion.path
                d={area}
                fill={`url(#${gid})`}
                initial={draw ? { opacity: 0 } : false}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: draw ? 0.25 : 0, ease: easings.standard }}
              />
            </>
          ) : null}
          <motion.path
            d={line}
            fill="none"
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={draw ? { pathLength: 0 } : false}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.8, ease: easings.snappy }}
          />
          {showDot && last ? (
            <motion.circle
              cx={last[0]}
              cy={last[1]}
              r={strokeWidth + 1}
              fill={stroke}
              stroke="var(--color-surface)"
              strokeWidth={2}
              initial={draw ? { scale: 0, opacity: 0 } : false}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: draw ? 0.7 : 0, type: 'spring', stiffness: 500, damping: 26 }}
            />
          ) : null}
        </svg>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- NumberTicker
type TickerFormat = 'money' | 'integer' | 'decimal' | 'percent' | ((n: number) => string);

function makeFormatter(format: TickerFormat, decimals?: number): (n: number) => string {
  if (typeof format === 'function') return format;
  if (format === 'money') return (n) => formatMoney(Math.round(n));
  if (format === 'percent') {
    const f = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: decimals ?? 1, minimumFractionDigits: 0 });
    return (n) => `${f.format(n)}%`;
  }
  const digits = format === 'decimal' ? (decimals ?? 2) : 0;
  const f = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: digits, maximumFractionDigits: digits });
  return (n) => f.format(format === 'integer' ? Math.round(n) : n);
}

export interface NumberTickerProps {
  /** Valor final. Em `money`, em centavos (como o resto do app). */
  value: number;
  format?: TickerFormat;
  decimals?: number;
  /** Valor inicial da primeira contagem (padrão 0). */
  from?: number;
  /** Duração em segundos (padrão 0,9). */
  duration?: number;
  /** Só começa a contar quando aparece na tela (padrão true). */
  startOnView?: boolean;
  className?: string;
}

/**
 * Número que sobe contando até o valor (dinheiro, inteiros, percentuais) no formato pt-BR.
 * Leitores de tela recebem só o valor final. Com "reduzir movimento", mostra o valor direto.
 */
export function NumberTicker({ value, format = 'integer', decimals, from = 0, duration = 0.9, startOnView = true, className }: NumberTickerProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const reduce = useReducedMotion();
  const inView = useInView(ref, { once: true, margin: '0px 0px -8% 0px' });
  const formatter = useMemo(() => makeFormatter(format, decimals), [format, decimals]);
  const current = useRef(from);
  const [initial] = useState(() => (reduce ? value : from));

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (reduce) {
      current.current = value;
      el.textContent = formatter(value);
      return;
    }
    if (startOnView && !inView) return;
    const controls = animate(current.current, value, {
      duration,
      ease: easings.snappy,
      onUpdate: (v) => {
        current.current = v;
        el.textContent = formatter(v);
      },
    });
    return () => controls.stop();
  }, [value, inView, reduce, startOnView, duration, formatter]);

  return (
    <span className={cn('tabular', className)}>
      <span ref={ref} aria-hidden="true" className="print:hidden">
        {formatter(initial)}
      </span>
      {/* leitores de tela e impressão recebem sempre o valor final */}
      <span className="sr-only print:not-sr-only">{formatter(value)}</span>
    </span>
  );
}

// ---------------------------------------------------------------- KpiCard
export interface KpiCardProps {
  label: ReactNode;
  /** Valor já formatado (ou um <NumberTicker/> / <Money/>). */
  value: ReactNode;
  /** Variação percentual vs. período anterior. */
  delta?: number | null;
  /** Complemento da variação, ex.: "vs. ontem". */
  deltaLabel?: ReactNode;
  /** Para custos e devoluções: cair é bom. */
  invertDelta?: boolean;
  /** Série para o minigráfico. */
  sparkline?: number[];
  sparklineTone?: Tone;
  icon?: ReactNode;
  hint?: ReactNode;
  loading?: boolean;
  onClick?: () => void;
  className?: string;
}

/** Cartão de indicador: rótulo, valor grande, variação com seta e minigráfico opcional. */
export function KpiCard({
  label,
  value,
  delta,
  deltaLabel,
  invertDelta,
  sparkline,
  sparklineTone,
  icon,
  hint,
  loading,
  onClick,
  className,
}: KpiCardProps) {
  const hasDelta = delta !== undefined && delta !== null;
  const tone: Tone = sparklineTone ?? (hasDelta ? ((invertDelta ? -delta! : delta!) >= 0 ? 'primary' : 'danger') : 'primary');
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'flex h-full min-w-0 flex-col rounded-card border border-line bg-surface p-4 text-left shadow-card sm:p-5',
        onClick &&
          'transition-[box-shadow,translate,border-color,scale] duration-200 ease-standard hover:-translate-y-px hover:border-line-strong hover:shadow-raised active:scale-[0.99] motion-reduce:hover:translate-y-0',
        className,
      )}
    >
      <div className="flex h-7 items-center justify-between gap-3">
        <span className="truncate text-[13px] font-medium text-ink-2">{label}</span>
        {icon ? (
          <span className="-mr-1 flex size-7 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary-strong [&_svg]:size-4">
            {icon}
          </span>
        ) : null}
      </div>
      {loading ? (
        <div className="mt-2 space-y-2">
          <Skeleton className="h-7 w-2/3" />
          <Skeleton className="h-3.5 w-1/3" />
        </div>
      ) : (
        <>
          <div className="tabular mt-0.5 truncate text-[26px] font-semibold leading-tight tracking-[-0.03em] text-ink sm:text-[28px]">{value}</div>
          {hasDelta || hint ? (
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
              {hasDelta ? <Delta value={delta!} invert={invertDelta} suffix={deltaLabel} /> : null}
              {hint ? <span className="text-xs text-ink-3">{hint}</span> : null}
            </div>
          ) : null}
        </>
      )}
      {sparkline && sparkline.length > 1 && !loading ? (
        <div className="mt-auto pt-3">
          <Sparkline data={sparkline} tone={tone} height={40} className="-mb-1" />
        </div>
      ) : null}
    </Tag>
  );
}

// ---------------------------------------------------------------- InsightCard
/** Trecho destacado em lima dentro do título do InsightCard. */
export function InsightHighlight({ children }: { children: ReactNode }) {
  return <span className="text-accent">{children}</span>;
}

/**
 * Cartão escuro do módulo "Inteligência": rótulo em caixa alta, frase curta com o número que
 * importa em lima, explicação e ação. É uma ilha escura (data-theme="dark") nos dois temas.
 */
export function InsightCard({
  eyebrow = 'Inteligência',
  title,
  children,
  action,
  icon,
  className,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
  className?: string;
}) {
  return (
    <section
      data-theme="dark"
      className={cn(
        'relative isolate flex flex-col overflow-hidden rounded-card bg-dark p-5 text-on-dark shadow-raised ring-1 ring-inset ring-white/[0.08] sm:p-6',
        className,
      )}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-20 -top-28 -z-10 size-72 rounded-full bg-[radial-gradient(closest-side,rgb(114_89_242/0.34),transparent)]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-20 right-1/4 -z-10 h-40 w-72 rounded-full bg-[radial-gradient(closest-side,rgb(200_241_105/0.06),transparent)]"
      />
      <div className="flex items-center gap-2 text-white/60">
        {icon ? <span className="text-accent [&_svg]:size-4">{icon}</span> : <span aria-hidden="true" className="size-1.5 rounded-full bg-accent" />}
        <span className="eyebrow">{eyebrow}</span>
      </div>
      <h3 className="mt-2.5 text-balance text-[21px] font-semibold leading-snug tracking-[-0.03em] text-white sm:text-[22px]">{title}</h3>
      {children ? <div className="mt-1.5 max-w-prose text-sm leading-relaxed text-white/70">{children}</div> : null}
      {action ? <div className="mt-auto flex flex-wrap gap-2 pt-4">{action}</div> : null}
    </section>
  );
}

// ---------------------------------------------------------------- ProgressRing
const RING_STROKE: Record<Exclude<Tone, 'neutral' | 'info'>, string> = {
  primary: 'var(--color-primary)',
  success: 'var(--color-success)',
  warning: 'var(--color-warning)',
  danger: 'var(--color-danger)',
  accent: 'var(--color-accent)',
};

/** Anel de progresso (metas, uso do plano, etapas concluídas). */
export function ProgressRing({
  value,
  max = 100,
  size = 56,
  strokeWidth = 6,
  tone = 'primary',
  label,
  showValue = true,
  className,
  'aria-label': ariaLabel,
}: {
  value: number;
  max?: number;
  size?: number;
  strokeWidth?: number;
  tone?: keyof typeof RING_STROKE;
  /** Conteúdo no centro (padrão: percentual). */
  label?: ReactNode;
  showValue?: boolean;
  className?: string;
  'aria-label'?: string;
}) {
  const reduce = useReducedMotion();
  const pct = max > 0 ? Math.min(1, Math.max(0, value / max)) : 0;
  const r = (size - strokeWidth) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div
      role="progressbar"
      aria-label={ariaLabel}
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
      className={cn('relative inline-flex shrink-0 items-center justify-center', className)}
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90" aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeWidth={strokeWidth} className="text-ink/[0.08]" />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={RING_STROKE[tone]}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={c}
          initial={reduce ? false : { strokeDashoffset: c }}
          animate={{ strokeDashoffset: c * (1 - pct) }}
          transition={{ duration: reduce ? 0 : 0.9, ease: easings.snappy }}
        />
      </svg>
      {showValue ? (
        <span className="tabular absolute inset-0 flex items-center justify-center text-[13px] font-semibold tracking-[-0.02em] text-ink">
          {label ?? `${Math.round(pct * 100)}%`}
        </span>
      ) : null}
    </div>
  );
}
